/**
 * Stock Audit — physical inventory count.
 *
 * Blind count: the counter NEVER sees book quantity. Two methods, same count:
 *   • Barcode sweep  — scan → +1 (repeat scans add up)
 *   • Search & type  — find the item, type the TOTAL counted quantity
 *
 * Starting a count FREEZES stock-out at that location; submitting unfreezes it.
 * Only company admins can start / approve / cancel. Approval posts a SAJ adjustment.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getItemsCached, getLocationsCached, getCategoriesCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { colors } from '../../theme/colors';
import { FiPlus, FiSearch, FiTrash2, FiCheck, FiX, FiArrowLeft, FiAlertTriangle, FiLock, FiExternalLink } from 'react-icons/fi';
import useCurrency from '../../hooks/useCurrency';
import { createTransaction } from '../../utils/transactionHelper';
import {
  loadAudits, loadAuditLines, startAudit, saveAuditLine, submitAudit, updateLineCount,
  buildAdjustmentSplit, markApproved, rejectAudit, cancelAudit, getLocationStock,
  loadAudit, auditDocRef, isFullCountAudit, recordAuditVouchers,
} from '../../utils/stockAuditHelper';
import { fmtDateTime, duration } from '../reports/auditReportStyles';

const StockAudit = () => {
  const { activeCompany, currentUser, companyUser, userProfile, activeBranch, activeFinancialYear } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const companyId = activeCompany?.id;
  const isAdmin = companyUser?.role === 'admin' || activeCompany?.ownerId === currentUser?.uid;

  // Open the Add Stock / Reduce Stock voucher this audit posted.
  const openVoucher = (type, txnId) => {
    if (!txnId) return;
    navigate(type === 'AS' ? '/store/add-stock' : '/store/reduce-stock', { state: { viewTransactionId: txnId } });
  };

  // The posted-voucher chips, shown once an audit is Approved.
  const Vouchers = ({ a }) => {
    if (!a.postedReduceStockNumber && !a.postedAddStockNumber) return null;
    return (
      <VoucherWrap>
        {a.postedReduceStockNumber && (
          <VoucherChip $out onClick={() => openVoucher('RS', a.postedReduceStockId)} title="Open the Reduce Stock voucher">
            {a.postedReduceStockNumber} <FiExternalLink size={11} />
          </VoucherChip>
        )}
        {a.postedAddStockNumber && (
          <VoucherChip onClick={() => openVoucher('AS', a.postedAddStockId)} title="Open the Add Stock voucher">
            {a.postedAddStockNumber} <FiExternalLink size={11} />
          </VoucherChip>
        )}
      </VoucherWrap>
    );
  };

  const [view, setView] = useState('list'); // list | count | review
  const [audits, setAudits] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Start form
  const [startBranchId, setStartBranchId] = useState('');
  const [startLocationId, setStartLocationId] = useState('');
  // INV-10: PARTIAL (only counted items are adjusted) is the safe default.
  const [startCountMode, setStartCountMode] = useState('partial');

  // Counting
  const [audit, setAudit] = useState(null);
  const [lines, setLines] = useState({});        // itemId -> line
  const [bookStock, setBookStock] = useState({}); // loaded ONCE (location is frozen)
  const [scan, setScan] = useState('');
  const [search, setSearch] = useState('');
  const [qtyFor, setQtyFor] = useState(null);     // item awaiting a typed qty
  const [qtyVal, setQtyVal] = useState('');
  const [toast, setToast] = useState('');
  const scanRef = useRef(null);

  // Review
  const [reviewLines, setReviewLines] = useState([]);
  const [diffIds, setDiffIds] = useState(new Set()); // rows shown by default (had a difference)
  const [showAllLines, setShowAllLines] = useState(false);
  const [drafts, setDrafts] = useState({});          // itemId -> in-progress edit

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2200); };

  // ── Load ──
  const refresh = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [a, itemRows, locRows, brSnap, catRows] = await Promise.all([
        loadAudits(companyId),
        // Tier 1: items come from the device-local master cache (0 Firestore reads/open).
        getItemsCached(companyId),
        getLocationsCached(companyId),
        getDocs(collection(db, 'companies', companyId, 'branches')).catch(() => ({ docs: [] })),
        // Category names for the counting-screen breakup (session-cached, ~1 read once).
        getCategoriesCached(companyId),
      ]);
      setAudits(a);
      setItems(itemRows.filter(i => i.isActive !== false && i.isDeleted !== true));
      setCategories(catRows || []);
      setLocations(locRows.filter(l => l.isActive !== false));
      setBranches((brSnap.docs || []).map(d => ({ id: d.id, ...d.data() })).filter(b => b.isActive !== false));
    } catch (e) { console.error('StockAudit load:', e); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-select when there's only one real choice (small traders see no dropdowns).
  useEffect(() => {
    if (branches.length === 1) setStartBranchId(branches[0].id);
    else if (activeBranch?.id && activeBranch.id !== 'all') setStartBranchId(activeBranch.id);
  }, [branches, activeBranch]);

  const locationsForBranch = useMemo(() => {
    // A branch points at ONE location, but a branch can physically hold several.
    // We don't force it — show all locations; the branch just pre-selects its own.
    return locations;
  }, [locations]);

  useEffect(() => {
    if (locationsForBranch.length === 1) { setStartLocationId(locationsForBranch[0].id); return; }
    const br = branches.find(b => b.id === startBranchId);
    if (br?.defaultLocationId) setStartLocationId(br.defaultLocationId);
  }, [locationsForBranch, branches, startBranchId]);

  // ── Start ──
  const handleStart = async () => {
    if (!isAdmin) { flash('Only an admin can start a count.'); return; }
    const loc = locations.find(l => l.id === startLocationId);
    if (!loc) { flash('Pick a location.'); return; }
    const modeText = startCountMode === 'full'
      ? 'FULL count — anything with stock here that you do NOT count is treated as 0 (a shortage).'
      : 'PARTIAL count — only the items you count are adjusted; everything else is left as it is.';
    if (!window.confirm(`Start counting "${loc.locationName}"?\n\n${modeText}\n\nSales and stock-out at this location will be PAUSED until you submit the count.`)) return;
    // Resolve the branch: what they picked, else the branch that points at this
    // location. With more than one branch we insist, so the audit list is never
    // ambiguous about which branch a location belongs to.
    const br = branches.find(b => b.id === startBranchId)
      || branches.find(b => b.defaultLocationId === loc.id)
      || null;
    if (branches.length > 1 && !br) { flash('Pick the branch this location belongs to.'); return; }

    setBusy(true);
    try {
      const id = await startAudit(companyId, {
        branchId: br?.id || null, branchName: br?.branchName || null,
        locationId: loc.id, locationName: loc.locationName,
        countMode: startCountMode,
      }, { uid: currentUser.uid, name: userProfile?.displayName || '' });
      await refresh();
      const fresh = (await loadAudits(companyId)).find(a => a.id === id);
      await openCount(fresh);
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  // ── Counting ──
  const openCount = async (a) => {
    setBusy(true);
    try {
      const [existing, stock] = await Promise.all([
        loadAuditLines(companyId, a.id),
        getLocationStock(companyId, a.locationId), // ONCE — location is frozen
      ]);
      const map = {};
      existing.forEach(l => { map[l.itemId] = l; });
      setAudit(a); setLines(map); setBookStock(stock);
      setView('count');
      setTimeout(() => scanRef.current?.focus(), 100);
    } catch (e) { alert('Could not open the count: ' + e.message); }
    finally { setBusy(false); }
  };

  // mode 'scan' adds to the running qty; 'set' replaces it (typed total);
  // 'remove' takes the item off the count (INV-10: a partial count leaves it untouched).
  const addCount = useCallback(async (item, qty, mode) => {
    if (!item || !audit) return;
    const prev = lines[item.id];
    const countedQty = mode === 'scan' ? (Number(prev?.countedQty) || 0) + qty : qty;
    const line = {
      itemId: item.id,
      itemCode: item.itemCode || '',
      itemName: item.itemName || '',
      barcode: item.barcode || '',
      unitOfMeasure: item.unitOfMeasure || 'Pcs',
      countedQty,
      // Book qty snapshotted from the one-time load; never shown to the counter.
      bookQty: prev ? prev.bookQty : Number(bookStock[item.id] || 0),
      unitCost: prev ? prev.unitCost : (Number(item.averageCost) || Number(item.purchasePrice) || 0),
      removed: mode === 'remove',
    };
    setLines(p => ({ ...p, [item.id]: line }));
    try { await saveAuditLine(companyId, audit.id, line, { uid: currentUser.uid }); }
    catch (e) { flash('Save failed — check connection.'); }
  }, [audit, lines, bookStock, companyId, currentUser]);

  const handleScan = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const code = scan.trim();
    if (!code) return;
    const item = items.find(i => (i.barcode || '') === code) || items.find(i => (i.itemCode || '').toLowerCase() === code.toLowerCase());
    if (!item) { flash(`Not found: ${code}`); setScan(''); return; }
    addCount(item, 1, 'scan');
    flash(`+1 ${item.itemName}`);
    setScan('');
  };

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return items.filter(i =>
      (i.itemName || '').toLowerCase().includes(q) ||
      (i.itemCode || '').toLowerCase().includes(q) ||
      (i.barcode || '').includes(q)
    ).slice(0, 15);
  }, [search, items]);

  const commitQty = async () => {
    const q = Number(qtyVal);
    if (!qtyFor || isNaN(q) || q < 0) { flash('Enter a valid quantity.'); return; }
    await addCount(qtyFor, q, 'set');
    flash(`${qtyFor.itemName} = ${q}`);
    setQtyFor(null); setQtyVal(''); setSearch('');
  };

  const removeLine = async (itemId) => {
    await addCount(items.find(i => i.id === itemId), 0, 'remove');
  };

  const handleSubmit = async () => {
    const full = isFullCountAudit(audit);
    const n = full ? Object.keys(lines).length : countedList.length;
    if (n === 0) { flash('Count at least one item.'); return; }
    const rest = full
      ? 'Anything with stock here that you did NOT count will be listed as a shortage for the admin to review.'
      : 'Only the items you counted will be adjusted — nothing else is changed.';
    if (!window.confirm(`Submit this count?\n\n${n} item(s) counted. ${rest}\n\nSales at this location resume immediately.`)) return;
    setBusy(true);
    try {
      const itemsById = {};
      items.forEach(i => { itemsById[i.id] = i; });
      await submitAudit(companyId, audit, { uid: currentUser.uid }, itemsById);
      await refresh();
      setView('list'); setAudit(null); setLines({});
    } catch (e) { alert('Submit failed: ' + e.message); }
    finally { setBusy(false); }
  };

  // ── Review / approve ──
  const openReview = async (a) => {
    setBusy(true);
    try {
      const ls = await loadAuditLines(companyId, a.id);
      ls.sort((x, y) => Math.abs(Number(y.variance) || 0) - Math.abs(Number(x.variance) || 0));
      // Rows shown by default = the differences. Membership is fixed on open, so a
      // row doesn't vanish mid-edit when the admin types it back to zero.
      setDiffIds(new Set(ls.filter(l => Math.abs(Number(l.variance) || 0) > 0.0001).map(l => l.itemId)));
      setAudit(a); setReviewLines(ls); setDrafts({}); setShowAllLines(false); setView('review');
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  /** Admin manually corrects a counted quantity in the review, before approving. */
  const editCount = async (line, raw) => {
    const val = Number(raw);
    if (isNaN(val) || val < 0) { flash('Enter a valid quantity.'); return; }
    if (val === (Number(line.countedQty) || 0)) return;
    try {
      const updated = await updateLineCount(companyId, audit.id, line, val);
      setReviewLines(ls => ls.map(l => (l.itemId === line.itemId ? updated : l)));
    } catch (e) { flash('Could not save the change.'); }
  };

  const handleApprove = async () => {
    if (!isAdmin) { flash('Only an admin can approve.'); return; }
    const { excess, shortage } = buildAdjustmentSplit(reviewLines, audit.locationId, audit.locationName);

    if (excess.length === 0 && shortage.length === 0) {
      if (!window.confirm('No differences — nothing to adjust. Mark this audit as approved?')) return;
      setBusy(true);
      try { await markApproved(companyId, audit.id, {}, { uid: currentUser.uid }); await refresh(); setView('list'); }
      catch (e) { alert(e.message); } finally { setBusy(false); }
      return;
    }
    if (!activeFinancialYear?.id) { alert('Activate a financial year first.'); return; }

    const parts = [];
    if (shortage.length) parts.push(`• Reduce Stock voucher — ${shortage.length} short item(s)`);
    if (excess.length) parts.push(`• Add Stock voucher — ${excess.length} excess item(s)`);
    if (!window.confirm(`Approve and adjust stock?\n\n${parts.join('\n')}\n\nThis posts the voucher(s) above and cannot be undone.`)) return;

    setBusy(true);
    try {
      // INV-10: approval is safe to retry. Each voucher is recorded on the audit in
      // the SAME save that posts it, and a retry (e.g. after a dropped connection)
      // re-reads the audit and skips any voucher it already posted.
      const fresh = (await loadAudit(companyId, audit.id)) || audit;
      if (fresh.status !== 'PendingApproval') {
        alert(`This count is already ${fresh.status === 'Approved' ? 'approved' : fresh.status} — nothing more to post.`);
        await refresh(); setView('list');
        return;
      }
      const docDate = new Date().toISOString().split('T')[0];
      const base = {
        docDate,
        financialYearId: activeFinancialYear.id,
        branchId: audit.branchId || null,
        branchName: audit.branchName || null,
        locationId: audit.locationId,
        locationName: audit.locationName,
        status: 'Posted',
      };
      const sum = (ls) => ls.reduce((s, l) => s + (Number(l.totalCost) || 0), 0);

      let reduceStockId = fresh.postedReduceStockId || null, reduceStockNumber = fresh.postedReduceStockNumber || null;
      let addStockId = fresh.postedAddStockId || null, addStockNumber = fresh.postedAddStockNumber || null;

      // Posts one voucher and records it on the audit in the same save.
      const postVoucher = async (data, idKey, numKey) => {
        let recorded = false;
        const t = await createTransaction(companyId, data, currentUser.uid, {
          extraWrites: async (w, ctx) => {
            recorded = true;
            w.update(auditDocRef(companyId, audit.id), { [idKey]: ctx.transactionId, [numKey]: ctx.docNumber });
          },
        });
        if (!recorded) {
          await recordAuditVouchers(companyId, audit.id, { [idKey]: t?.id || null, [numKey]: t?.docNumber || null });
        }
        return t;
      };

      // Shortage → Reduce Stock (existing voucher)
      if (shortage.length && !reduceStockId) {
        const t = await postVoucher({
          ...base, tranTypeCode: 'RS', lines: shortage,
          subtotal: sum(shortage), totalAmount: sum(shortage),
          notes: `Stock audit shortage — ${audit.locationName}`,
        }, 'postedReduceStockId', 'postedReduceStockNumber');
        reduceStockId = t?.id || null;
        reduceStockNumber = t?.docNumber || null;
      }

      // Excess → Add Stock (existing voucher)
      if (excess.length && !addStockId) {
        const t = await postVoucher({
          ...base, tranTypeCode: 'AS', lines: excess,
          subtotal: sum(excess), totalAmount: sum(excess),
          notes: `Stock audit excess — ${audit.locationName}`,
        }, 'postedAddStockId', 'postedAddStockNumber');
        addStockId = t?.id || null;
        addStockNumber = t?.docNumber || null;
      }

      await markApproved(companyId, audit.id,
        { addStockId, addStockNumber, reduceStockId, reduceStockNumber },
        { uid: currentUser.uid });
      await refresh();
      setView('list');
      flash('Stock adjusted.');
    } catch (e) { alert('Approve failed: ' + e.message + '\n\nYou can press Approve again — anything already posted will not be posted twice.'); }
    finally { setBusy(false); }
  };

  const handleReject = async () => {
    if (!isAdmin) { flash('Only an admin can reject.'); return; }
    if (!window.confirm('Send this back for a recount? No stock will be changed.')) return;
    setBusy(true);
    try { await rejectAudit(companyId, audit, { uid: currentUser.uid }); await refresh(); setView('list'); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const handleCancel = async (a) => {
    if (!isAdmin) { flash('Only an admin can cancel.'); return; }
    if (!window.confirm(`Cancel the count at ${a.locationName}?\n\nNothing is adjusted, and sales at that location resume immediately.`)) return;
    setBusy(true);
    try { await cancelAudit(companyId, a, { uid: currentUser.uid }); await refresh(); setView('list'); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  // A line is counted unless it was taken off the count (INV-10). Lines saved
  // before the `removed` flag existed keep the old rule (counted qty above 0).
  const countedList = useMemo(() => Object.values(lines).filter(l => (
    l.removed === false || (l.removed === undefined && Number(l.countedQty) > 0)
  )), [lines]);

  // Live totals + main-category breakup of the COUNTED quantities (blind-safe:
  // only counted qty, never book/system qty). Resolves each item's main category.
  const countSummary = useMemo(() => {
    const catNameById = {}; categories.forEach(c => { catNameById[c.id] = c.categoryName || ''; });
    const itemCatById = {}; items.forEach(i => { itemCatById[i.id] = i.categoryId || ''; });
    let totalPieces = 0;
    const byCat = {};
    countedList.forEach(l => {
      const qty = Number(l.countedQty) || 0;
      totalPieces += qty;
      const name = catNameById[itemCatById[l.itemId] || ''] || 'Uncategorized';
      byCat[name] = (byCat[name] || 0) + qty;
    });
    const rows = Object.entries(byCat).map(([name, qty]) => ({ name, qty })).sort((a, b) => b.qty - a.qty);
    return { totalItems: countedList.length, totalPieces: Math.round(totalPieces * 100) / 100, rows };
  }, [countedList, items, categories]);

  // ═══════════════════════ LIST ═══════════════════════
  if (view === 'list') {
    const openOne = audits.find(a => a.status === 'Counting');
    return (
      <PageLayout pageTitle="Stock Audit" breadcrumb="Store / Stock Audit">
        {toast && <Toast>{toast}</Toast>}

        {openOne && (
          <Warn>
            <FiLock />
            <div>
              <b>Count in progress at {openOne.branchName ? `${openOne.branchName} · ` : ''}{openOne.locationName}</b> — sales and stock-out at this location are paused.
            </div>
            <Btn onClick={() => openCount(openOne)}>Continue counting</Btn>
            {isAdmin && <Btn $danger onClick={() => handleCancel(openOne)}>Cancel count</Btn>}
          </Warn>
        )}

        {isAdmin && !openOne && (
          <Card>
            <H3>Start a new count</H3>
            <Row>
              {branches.length > 1 && (
                <Field>
                  <Lbl>Branch</Lbl>
                  <Select value={startBranchId} onChange={e => setStartBranchId(e.target.value)}>
                    <option value="">— Select —</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                  </Select>
                </Field>
              )}
              {locationsForBranch.length > 1 && (
                <Field>
                  <Lbl>Location *</Lbl>
                  <Select value={startLocationId} onChange={e => setStartLocationId(e.target.value)}>
                    <option value="">— Select —</option>
                    {locationsForBranch.map(l => <option key={l.id} value={l.id}>{l.locationName}</option>)}
                  </Select>
                </Field>
              )}
              <Field>
                <Lbl>Count type</Lbl>
                <Select value={startCountMode} onChange={e => setStartCountMode(e.target.value)}>
                  <option value="partial">Partial count — only counted items</option>
                  <option value="full">Full count — whole location</option>
                </Select>
              </Field>
              <Field>
                <Lbl>&nbsp;</Lbl>
                <Primary onClick={handleStart} disabled={busy || !startLocationId}><FiPlus /> Start Count</Primary>
              </Field>
            </Row>
            <Note>
              Starting a count <b>pauses sales</b> at that location until you submit. Best done after closing.
              {startCountMode === 'full'
                ? <> Full count: anything you don't count that still has stock here is listed as a <b>shortage</b> for you to review.</>
                : <> Partial count: only the items you count are adjusted — <b>nothing else is changed</b>.</>}
            </Note>
          </Card>
        )}

        <Card>
          <H3>Audit history</H3>
          {loading ? <Muted>Loading…</Muted> : audits.length === 0 ? <Muted>No counts yet.</Muted> : (
            <Table>
              <thead><tr>
                {branches.length > 1 && <Th>Branch</Th>}
                <Th>Location</Th><Th>Started</Th><Th>Ended</Th><Th>Took</Th><Th>Status</Th>
                <Th align="right">Items</Th><Th align="right">Shortage</Th><Th align="right">Excess</Th>
                <Th>Vouchers</Th><Th>Actions</Th>
              </tr></thead>
              <tbody>
                {audits.map(a => (
                  <tr key={a.id}>
                    {branches.length > 1 && <Td>{a.branchName || '—'}</Td>}
                    <Td><b>{a.locationName}</b></Td>
                    <Td>{fmtDateTime(a.startedAt)}</Td>
                    <Td>{a.submittedAt ? fmtDateTime(a.submittedAt) : <span style={{ color: '#999' }}>—</span>}</Td>
                    <Td>{duration(a.startedAt, a.submittedAt)}</Td>
                    <Td><Status $s={a.status}>{a.status === 'PendingApproval' ? 'Pending Approval' : a.status}</Status></Td>
                    <Td align="right">{a.totals?.itemsCounted ?? '—'}</Td>
                    <Td align="right" style={{ color: colors.error.main }}>{a.totals ? formatCurrency(a.totals.shortageValue) : '—'}</Td>
                    <Td align="right" style={{ color: colors.success.main }}>{a.totals ? formatCurrency(a.totals.excessValue) : '—'}</Td>
                    <Td><Vouchers a={a} /></Td>
                    <Td>
                      {a.status === 'Counting' && <Btn onClick={() => openCount(a)}>Continue</Btn>}
                      {(a.status === 'PendingApproval' || a.status === 'Approved' || a.status === 'Rejected') && (
                        <Btn onClick={() => openReview(a)}>{a.status === 'PendingApproval' && isAdmin ? 'Review' : 'View'}</Btn>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </PageLayout>
    );
  }

  // ═══════════════════════ COUNT (blind) ═══════════════════════
  if (view === 'count' && audit) {
    return (
      <PageLayout pageTitle="Stock Audit" breadcrumb="Store / Stock Audit / Counting">
        {toast && <Toast>{toast}</Toast>}
        <Head>
          <Btn onClick={() => { setView('list'); setAudit(null); }}><FiArrowLeft /> Back</Btn>
          <div><b>Counting: {audit.branchName ? `${audit.branchName} · ` : ''}{audit.locationName}</b> <Blind>BLIND COUNT — system quantity is hidden</Blind> <Blind>{isFullCountAudit(audit) ? 'FULL COUNT' : 'PARTIAL COUNT — only counted items are adjusted'}</Blind></div>
          <Primary onClick={handleSubmit} disabled={busy}><FiCheck /> Submit Count</Primary>
        </Head>

        <Card>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Stat><span>Total Items</span><b>{countSummary.totalItems}</b></Stat>
            <Stat><span>Total Pieces</span><b>{countSummary.totalPieces}</b></Stat>
            {countSummary.rows.length > 0 && (
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 12, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>By Category (pieces)</div>
                <Table>
                  <tbody>
                    {countSummary.rows.map(r => (
                      <tr key={r.name}><Td>{r.name}</Td><Td align="right"><b>{r.qty}</b></Td></tr>
                    ))}
                    <tr style={{ borderTop: '2px solid #e2e8f0' }}>
                      <Td><b>Total</b></Td><Td align="right"><b>{countSummary.totalPieces}</b></Td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <Row>
            <Field style={{ flex: 1 }}>
              <Lbl>Scan barcode (keep scanning — repeats add up)</Lbl>
              <Input ref={scanRef} value={scan} onChange={e => setScan(e.target.value)} onKeyDown={handleScan}
                placeholder="Scan or type a barcode, then press Enter" autoFocus />
            </Field>
            <Field style={{ flex: 1 }}>
              <Lbl>Or search an item and type the total quantity</Lbl>
              <SearchWrap>
                <FiSearch />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or code…" />
              </SearchWrap>
            </Field>
          </Row>

          {searchResults.length > 0 && !qtyFor && (
            <Results>
              {searchResults.map(i => (
                <ResultRow key={i.id} onClick={() => { setQtyFor(i); setQtyVal(''); }}>
                  <span>{i.itemName} <small>{i.itemCode}</small></span>
                  <Btn>Enter qty</Btn>
                </ResultRow>
              ))}
            </Results>
          )}

          {qtyFor && (
            <QtyBox>
              <b>{qtyFor.itemName}</b>
              <Input autoFocus type="number" min="0" step="any" value={qtyVal}
                onChange={e => setQtyVal(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitQty(); } }}
                placeholder="Total counted quantity" style={{ maxWidth: 200 }} />
              <Primary onClick={commitQty}>Save</Primary>
              <Btn onClick={() => { setQtyFor(null); setQtyVal(''); }}><FiX /></Btn>
            </QtyBox>
          )}
        </Card>

        <Card>
          <H3>Counted so far — {countSummary.totalItems} item(s) · {countSummary.totalPieces} piece(s)</H3>
          {countedList.length === 0 ? <Muted>Nothing counted yet. Start scanning.</Muted> : (
            <Table>
              <thead><tr><Th>Item</Th><Th>Code</Th><Th align="right">Counted</Th><Th></Th></tr></thead>
              <tbody>
                {countedList.slice().reverse().map(l => (
                  <tr key={l.itemId}>
                    <Td>{l.itemName}</Td>
                    <Td><small>{l.itemCode}</small></Td>
                    <Td align="right"><b>{l.countedQty}</b> {l.unitOfMeasure}</Td>
                    <Td><Btn $danger onClick={() => removeLine(l.itemId)}><FiTrash2 /></Btn></Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </PageLayout>
    );
  }

  // ═══════════════════════ REVIEW ═══════════════════════
  if (view === 'review' && audit) {
    const canAct = isAdmin && audit.status === 'PendingApproval';
    const shown = showAllLines ? reviewLines : reviewLines.filter(l => diffIds.has(l.itemId));
    // Totals recomputed live so the admin sees the effect of their edits immediately.
    const live = reviewLines.reduce((acc, l) => {
      const v = Number(l.variance) || 0;
      const val = Math.abs(v) * (Number(l.unitCost) || 0);
      if (Math.abs(v) > 0.0001) {
        acc.itemsWithVariance++;
        if (v < 0) acc.shortageValue += val; else acc.excessValue += val;
      }
      return acc;
    }, { itemsWithVariance: 0, shortageValue: 0, excessValue: 0 });
    return (
      <PageLayout pageTitle="Stock Audit" breadcrumb="Store / Stock Audit / Review">
        {toast && <Toast>{toast}</Toast>}
        <Head>
          <Btn onClick={() => { setView('list'); setAudit(null); }}><FiArrowLeft /> Back</Btn>
          <div><b>{audit.branchName ? `${audit.branchName} · ` : ''}{audit.locationName}</b> · <Status $s={audit.status}>{audit.status === 'PendingApproval' ? 'Pending Approval' : audit.status}</Status></div>
          {canAct && (
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn $danger onClick={handleReject} disabled={busy}>Reject / Recount</Btn>
              <Primary onClick={handleApprove} disabled={busy}><FiCheck /> Approve & Adjust</Primary>
            </div>
          )}
        </Head>

        <Card>
          <Row>
            <Stat><span>Items counted</span><b>{audit.totals?.itemsCounted ?? 0}</b></Stat>
            <Stat><span>With difference</span><b>{live.itemsWithVariance}</b></Stat>
            <Stat><span>Shortage value</span><b style={{ color: colors.error.main }}>{formatCurrency(live.shortageValue)}</b></Stat>
            <Stat><span>Excess value</span><b style={{ color: colors.success.main }}>{formatCurrency(live.excessValue)}</b></Stat>
          </Row>
          {audit.status === 'Approved' ? (
            <Posted>
              <FiCheck />
              <span>Posted as:</span>
              <Vouchers a={audit} />
              {!audit.postedAddStockNumber && !audit.postedReduceStockNumber && <i>no differences — nothing posted</i>}
            </Posted>
          ) : (
            <Note>
              <FiAlertTriangle />
              Approving posts up to two vouchers: a <b>Reduce Stock</b> for shortages and an <b>Add Stock</b> for excess.
              {canAct && <> You can correct any quantity below before approving.</>}
            </Note>
          )}
        </Card>

        <Card>
          <Head style={{ marginBottom: 12 }}>
            <H3 style={{ margin: 0 }}>Differences ({shown.length})</H3>
            <Check>
              <input type="checkbox" checked={showAllLines} onChange={e => setShowAllLines(e.target.checked)} />
              Show all counted items (to correct one that matched)
            </Check>
          </Head>
          {shown.length === 0 ? <Muted>No differences — the count matches the system exactly.</Muted> : (
            <Table>
              <thead><tr>
                <Th>Item</Th><Th align="right">System</Th><Th align="right">Counted{canAct && ' (editable)'}</Th>
                <Th align="right">Difference</Th><Th align="right">Value</Th>
              </tr></thead>
              <tbody>
                {shown.map(l => {
                  const v = Number(l.variance) || 0;
                  // INV-10: stock that came in (or moved) here while counting — already in System.
                  const moved = l.bookQtyAtStart !== undefined ? Math.round(((Number(l.bookQty) || 0) - (Number(l.bookQtyAtStart) || 0)) * 1000) / 1000 : 0;
                  return (
                    <tr key={l.itemId}>
                      <Td>
                        {l.itemName} <small>{l.itemCode}</small>
                        {l.notCounted && <Tag style={{ background: '#FFEBEE', color: '#C62828' }}>NOT COUNTED</Tag>}
                        {moved !== 0 && (
                          <Tag title="Stock received or moved at this location while it was being counted — already included in the System quantity">
                            MOVED DURING COUNT {moved > 0 ? '+' : ''}{moved}
                          </Tag>
                        )}
                      </Td>
                      <Td align="right">{l.bookQty}</Td>
                      <Td align="right">
                        {canAct ? (
                          <EditQty
                            type="number" min="0" step="any"
                            value={drafts[l.itemId] ?? String(l.countedQty ?? 0)}
                            onChange={e => setDrafts(d => ({ ...d, [l.itemId]: e.target.value }))}
                            onBlur={e => editCount(l, e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                          />
                        ) : l.countedQty}
                      </Td>
                      <Td align="right" style={{ color: v < 0 ? colors.error.main : v > 0 ? colors.success.main : '#999', fontWeight: 700 }}>
                        {v > 0 ? '+' : ''}{v}
                      </Td>
                      <Td align="right">{formatCurrency(Math.abs(v) * (Number(l.unitCost) || 0))}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>
      </PageLayout>
    );
  }

  return <PageLayout pageTitle="Stock Audit" breadcrumb="Store / Stock Audit"><Muted>Loading…</Muted></PageLayout>;
};

// ── Styles ──
const Card = styled.div`background:#fff;border-radius:10px;box-shadow:0 1px 3px rgba(0,0,0,0.08);padding:18px;margin-bottom:16px;`;
const H3 = styled.h3`margin:0 0 12px;font-size:15px;color:${colors.text.primary};`;
const Row = styled.div`display:flex;gap:14px;flex-wrap:wrap;align-items:flex-end;`;
const Field = styled.div`display:flex;flex-direction:column;gap:6px;min-width:180px;`;
const Lbl = styled.label`font-size:11px;font-weight:600;color:${colors.text.secondary};text-transform:uppercase;`;
const Input = styled.input`padding:10px 12px;border:1px solid ${colors.neutral[300]};border-radius:6px;font-size:14px;width:100%;&:focus{outline:none;border-color:${colors.primary.main};}`;
const Select = styled.select`padding:10px 12px;border:1px solid ${colors.neutral[300]};border-radius:6px;font-size:14px;background:#fff;`;
const Check = styled.label`display:flex;align-items:center;gap:8px;font-size:13px;color:${colors.text.secondary};cursor:pointer;padding:10px 0;`;
const Btn = styled.button`display:inline-flex;align-items:center;gap:6px;padding:8px 12px;border:1px solid ${p => p.$danger ? '#f0c4c4' : colors.neutral[300]};background:#fff;color:${p => p.$danger ? colors.error.main : colors.text.primary};border-radius:6px;font-size:13px;font-weight:600;cursor:pointer;&:hover{background:${colors.neutral[50]};}&:disabled{opacity:.6;cursor:not-allowed;}`;
const Primary = styled.button`display:inline-flex;align-items:center;gap:6px;padding:10px 16px;background:${colors.primary.main};color:#fff;border:none;border-radius:6px;font-size:14px;font-weight:700;cursor:pointer;&:hover{opacity:.92;}&:disabled{opacity:.6;cursor:not-allowed;}`;
const Note = styled.div`display:flex;align-items:center;gap:8px;margin-top:12px;font-size:12px;color:${colors.text.secondary};`;
const Muted = styled.div`padding:20px;text-align:center;color:#999;font-size:13px;`;
const Table = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`padding:10px;text-align:${p => p.align || 'left'};font-size:11px;text-transform:uppercase;color:${colors.text.secondary};background:${colors.neutral[50]};border-bottom:2px solid ${colors.neutral[200]};`;
const Td = styled.td`padding:10px;text-align:${p => p.align || 'left'};font-size:13px;border-bottom:1px solid ${colors.neutral[100]};small{color:#999;margin-left:6px;}`;
const Status = styled.span`display:inline-block;padding:3px 9px;border-radius:12px;font-size:11px;font-weight:700;
  background:${p => p.$s === 'Approved' ? '#E8F5E9' : p.$s === 'Counting' ? '#FFF3E0' : p.$s === 'PendingApproval' ? '#E3F2FD' : '#FFEBEE'};
  color:${p => p.$s === 'Approved' ? '#2E7D32' : p.$s === 'Counting' ? '#E65100' : p.$s === 'PendingApproval' ? '#1565C0' : '#C62828'};`;
const Tag = styled.span`margin-left:8px;padding:2px 6px;background:#EDE7F6;color:#5E35B1;border-radius:4px;font-size:10px;font-weight:700;`;
const Warn = styled.div`display:flex;align-items:center;gap:12px;padding:14px 16px;background:#FFF3E0;border:1px solid #FFCC80;border-radius:8px;margin-bottom:16px;color:#E65100;font-size:14px;`;
const Head = styled.div`display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px;flex-wrap:wrap;`;
const Blind = styled.span`margin-left:10px;padding:3px 9px;background:#EDE7F6;color:#5E35B1;border-radius:12px;font-size:11px;font-weight:700;`;
const SearchWrap = styled.div`display:flex;align-items:center;gap:8px;svg{color:#999;}`;
const Results = styled.div`margin-top:12px;border:1px solid ${colors.neutral[200]};border-radius:6px;max-height:240px;overflow-y:auto;`;
const ResultRow = styled.div`display:flex;align-items:center;justify-content:space-between;padding:9px 12px;border-bottom:1px solid ${colors.neutral[100]};cursor:pointer;font-size:13px;&:hover{background:${colors.neutral[50]};}small{color:#999;margin-left:6px;}`;
const QtyBox = styled.div`display:flex;align-items:center;gap:10px;margin-top:12px;padding:12px;background:${colors.neutral[50]};border-radius:8px;flex-wrap:wrap;`;
const Stat = styled.div`display:flex;flex-direction:column;gap:3px;min-width:130px;span{font-size:11px;color:#999;text-transform:uppercase;}b{font-size:18px;}`;
const EditQty = styled.input`width:90px;padding:6px 8px;border:1px solid ${colors.primary.main}55;border-radius:6px;font-size:13px;font-weight:700;text-align:right;background:#FFFDF5;&:focus{outline:none;border-color:${colors.primary.main};}`;
const VoucherWrap = styled.div`display:flex;gap:6px;flex-wrap:wrap;`;
const VoucherChip = styled.button`
  display:inline-flex;align-items:center;gap:5px;padding:4px 9px;border-radius:5px;cursor:pointer;
  font-size:12px;font-weight:700;font-family:'SF Mono','Consolas',monospace;
  border:1px solid ${p => p.$out ? '#f0c4c4' : '#c8e6c9'};
  background:${p => p.$out ? '#FFEBEE' : '#E8F5E9'};
  color:${p => p.$out ? '#C62828' : '#2E7D32'};
  &:hover{filter:brightness(0.96);text-decoration:underline;}
  svg{opacity:.7;}
`;
const Posted = styled.div`display:flex;align-items:center;gap:10px;margin-top:12px;font-size:13px;color:${colors.text.secondary};i{color:#999;}svg{color:${colors.success.main};}`;
const Toast = styled.div`position:fixed;top:80px;right:24px;z-index:9999;background:#1A1A1A;color:#fff;padding:10px 16px;border-radius:8px;font-size:13px;font-weight:600;box-shadow:0 6px 20px rgba(0,0,0,.25);`;

export default StockAudit;
